import { TEST_STRUCTURE_REAL_SIZE } from '@modules/editor/src/plugins/grid';
import { getBaseTestTextureUrl } from '@modules/editor/src/plugins/grid/utils/testStructureUtils';

import {
  getCurrentView,
  getVisibleComponentIds,
  type SelectorState,
} from '$/pages/InspirationEditorPage/lib/selectors';
import { type Mapping } from '$/pages/InspirationEditorPage/lib/webglRenderer';
import { type DetailedInspiration } from '$/services/usecases/inspirations/mapper/inspiration.schema';
import { type Material } from '$/services/usecases/materials/mapper/materials';
import { type LayerRotation } from '$/services/usecases/projects/mapper/inspirationProjects';

export type MaterialMapping = {
  material: Material;
  rotation?: LayerRotation;
  harmonyMaterialIndex?: number;
};

export type InspirationGeometry = {
  corners: Array<[number, number]>;
  cornerLines?: Array<[[number, number], [number, number]]>;
};

type MappingState = SelectorState & {
  currentMappingIndex: number;
  materialMappingOverrides: Record<string, MaterialMapping>[];
  cornerOverwrites: Record<number, Record<string, InspirationGeometry>>;
  activeComponentId?: string;
  showTestStructure?: boolean;
};

const FALLBACK_TEXTURE_SIZE = 350;

const ROTATION_ANGLES: Record<LayerRotation, number> = {
  vertical: 0,
  horizontal: 90,
  'diagonal-left': 135,
  'diagonal-right': 45,
};

export const getInitialMaterialMappings = (
  inspiration: DetailedInspiration,
): Record<string, MaterialMapping> =>
  Object.fromEntries(
    Object.entries(inspiration.components).flatMap(([id, component]) => {
      const material =
        component.initialMaterialId &&
        inspiration.materials[component.initialMaterialId];
      return material
        ? [[id, { material, rotation: 'vertical' as const }]]
        : [];
    }),
  );

export const applyMaterialToMainWall = (
  inspiration: DetailedInspiration,
  mappings: Record<string, MaterialMapping>,
  material: Material,
) => {
  const { mainWallComponentId } = inspiration;
  const targetIds =
    mainWallComponentId && inspiration.components[mainWallComponentId]
      ? [mainWallComponentId]
      : Object.keys(inspiration.components).filter(
          (id) => inspiration.components[id].category === 'wall',
        );

  const next = { ...mappings };
  for (const id of targetIds) {
    next[id] = { ...next[id], material, rotation: 'vertical' };
  }
  return next;
};

export const buildMaterialMappings = (
  state: MappingState,
  opt: {
    viewIdOverride?: number;
    mappingOverride?: number;
  } = {},
): Mapping[] => {
  const { inspiration } = state;
  if (!inspiration) return [];

  const materialMappings =
    state.materialMappingOverrides[
      opt.mappingOverride ?? state.currentMappingIndex
    ];
  const viewId = opt.viewIdOverride ?? state.currentViewId;
  const currentView = getCurrentView(state, viewId);
  const visibleIds = new Set(getVisibleComponentIds(state, viewId));
  const viewOverwrites = state.cornerOverwrites[viewId] || {};

  if (!materialMappings || !currentView) return [];

  const testStructureId = state.showTestStructure
    ? state.activeComponentId
    : undefined;

  return Object.entries<MaterialMapping | undefined>({
    ...materialMappings,
    ...(testStructureId
      ? { [testStructureId]: materialMappings[testStructureId] }
      : null),
  })
    .filter(([id]) => visibleIds.has(id))
    .map(([id, materialMapping]) => {
      const view = currentView.components.find((v) => v.id === id)!;

      const geometry = state.editMasterData
        ? (viewOverwrites[id] ?? {
            corners: view.corners,
            cornerLines: view.cornerLines,
          })
        : { corners: view.corners, cornerLines: view.cornerLines };

      if (id === testStructureId) {
        return {
          mask: `mask_${id}`,
          corners: geometry.corners,
          cornerLines: geometry.cornerLines,
          object_width: view.objectWidth,
          object_height: view.objectHeight,
          material: getBaseTestTextureUrl(),
          is_texture_material: true,
          rotation: 0,
          real_texture_width: TEST_STRUCTURE_REAL_SIZE,
          real_texture_height: TEST_STRUCTURE_REAL_SIZE,
        };
      }

      const { material, rotation = 'horizontal' } = materialMapping!;
      const isTextureMaterial = !!material.texturePath;

      return {
        mask: `mask_${id}`,
        corners: geometry.corners,
        cornerLines: geometry.cornerLines,
        object_width: view.objectWidth,
        object_height: view.objectHeight,
        material: isTextureMaterial
          ? material.seamlessTextureUrl()
          : material.hex,
        is_texture_material: isTextureMaterial,
        rotation: ROTATION_ANGLES[rotation],
        real_texture_width: material.textureWidth || FALLBACK_TEXTURE_SIZE,
        real_texture_height: material.textureHeight || FALLBACK_TEXTURE_SIZE,
      };
    });
};
